







































































































































































import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import { State, Action, Getter, Mutation } from 'vuex-class';
import { isWithinInterval, isValid, isBefore, endOfDay } from 'date-fns';
import { Camera } from '@/models/camera';
import { Project } from '@/models/project';
import { Photo } from '@/models/photo';
import { Video } from '@/models/video';
import { TimelapseConfiguration } from '@/models/timelapseconfiguration';
import { StabilizationConfig, TemporalFilterConfig } from '@/models/timelapseconfiguration';
import { TimelapseConfigurationUpdateRequest } from '@/services/projects/projects.interfaces';
import { Crop, Position } from '@/models/enums';
import TimelapsePreview from '@/components/timelapsepreview/timelapsepreview.component.vue';
import TimelapseFilter from '@/components/timelapsefilter/timelapsefilter.component.vue';
import TimelapseExclusion from '@/components/timelapseexclusion/timelapseexclusion.component.vue';
import TimelapsePhoto from '@/components/timelapsephoto/timelapsephoto.component.vue';
import TimelapseSettings from '@/components/timelapsesettings/timelapsesettings.component.vue';
import ScenarioModal from '@/components/scenariomodal/scenariomodal.component.vue';
import MotionAnalyze from '@/views/MotionAnalyze.vue';
import TimelapseIntroOutro, { IntroOutroModel } from '@/views/TimelapseIntroOutro.vue';
import RaindropAnalyze from '@/views/RaindropAnalyze.vue';
// @ts-ignore: no d.ts file
import { vTeleport } from '@desislavsd/vue-teleport';
import { NotificationMessage, Socket } from '@/store/types';
import { Logger } from 'aws-amplify';
import AWS from 'aws-sdk';
import { Auth } from 'aws-amplify';
const logger = new Logger('Timelapse');

async function configureAWSCredentials() {
  try {
    const credentials = await Auth.currentCredentials();
    AWS.config.update({
      credentials: credentials,
      region: 'eu-west-3', // Remplacez par votre région AWS
    });
    //console.log('AWS Credentials configurés avec succès.');
  } catch (error) {
    console.error('Erreur lors de la configuration des credentials AWS:', error);
  }
}

type FlatKB = {
  kenburnsEnabled?: boolean;
  kbEasing?: string;
  kbStartX?: number;
  kbStartY?: number;
  kbStartW?: number;
  kbStartH?: number;
  kbEndX?: number;
  kbEndY?: number;
  kbEndW?: number;
  kbEndH?: number;
};
type StabWithFlat = StabilizationConfig & FlatKB;

// ➜ Quand on charge un timelapse qui a déjà un objet nested .kenburns : on le copie en flat
function inflateFlatFromNested(stab: any) {
  if (!stab || !stab.kenburns) return;
  const kb = stab.kenburns;
  stab.kenburnsEnabled = !!kb.enabled;
  stab.kbEasing = kb.easing || 'linear';
  const s = kb.start || { x: 0, y: 0, w: 1, h: 1 };
  const e = kb.end || { x: 0.1, y: 0.1, w: 0.8, h: 0.8 };
  stab.kbStartX = s.x;
  stab.kbStartY = s.y;
  stab.kbStartW = s.w;
  stab.kbStartH = s.h;
  stab.kbEndX = e.x;
  stab.kbEndY = e.y;
  stab.kbEndW = e.w;
  stab.kbEndH = e.h;
}

// ➜ Juste avant d’envoyer au backend : on reconstruit l’objet nested .kenburns attendu
function buildNestedKenBurnsFromFlat(stab: any) {
  // si rien d’activé, on peut supprimer la clé
  if (!stab || !stab.kenburnsEnabled) {
    delete stab.kenburns;
    return;
  }

  const sx = stab.kbStartX ?? 0,
    sy = stab.kbStartY ?? 0,
    sw = stab.kbStartW ?? 1,
    sh = stab.kbStartH ?? 1;
  const ex = stab.kbEndX ?? 0.1,
    ey = stab.kbEndY ?? 0.1,
    ew = stab.kbEndW ?? 0.8,
    eh = stab.kbEndH ?? 0.8;

  stab.kenburns = {
    enabled: !!stab.kenburnsEnabled,
    easing: stab.kbEasing || 'linear',
    start: { x: sx, y: sy, w: sw, h: sh },
    end: { x: ex, y: ey, w: ew, h: eh },
  };
}

function fromDdbNum(v: any, def = 0): number {
  if (v === null || v === undefined) return def;
  if (typeof v === 'number') return Number.isFinite(v) ? v : def;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : def;
  }
  if (typeof v === 'object') {
    if (v.N !== undefined) {
      const n = parseFloat(v.N);
      return Number.isFinite(n) ? n : def;
    }
    if (v.S !== undefined) {
      const n = parseFloat(v.S);
      return Number.isFinite(n) ? n : def;
    }
  }
  return def;
}

function normalizeMd(md: any): {
  score?: number;
  motion?: number;
  structure?: number;
  keypoints?: number;
  flow?: number;
  orient?: number;
} | null {
  if (!md) return null;
  const src = md.M ?? md; // supporte {M:{...}} (Dynamo brut) ET {...} (déjà dé-sérialisé)
  return {
    score: fromDdbNum(src.score, 0),
    motion: fromDdbNum(src.motion, 0),
    structure: fromDdbNum(src.structure, 0),
    keypoints: fromDdbNum(src.keypoints, 0),
    flow: fromDdbNum(src.flow, 0),
    orient: fromDdbNum(src.orient, 0),
  };
}

function parseARString(s: string): number | null {
  if (!s) return null;

  // Normalisations agressives
  let t = s
    .trim()
    .toLowerCase()
    .replace(/,/g, '.') // 2,39 -> 2.39
    .replace(/[×x]/g, ':') // 1080x1920 / 1080×1920 -> 1080:1920
    .replace(/\//g, ':') // 9/16 -> 9:16
    .replace(/[^\d/:]/g, ''); // enlève tout ce qui n'est pas chiffre, point, deux-points

  if (!t) return null;

  const parts = t.split(':').filter(Boolean);

  // Cas "décimal pur" : "2.39" ou "2.39:1" ou "2,39"
  if (parts.length === 1) {
    const v = parseFloat(parts[0]);
    return Number.isFinite(v) && v > 0 ? v : null;
  }

  if (parts.length >= 2) {
    const a = parseFloat(parts[0]);
    const b = parseFloat(parts[1]);
    if (Number.isFinite(a) && Number.isFinite(b) && b > 0) return a / b;
  }

  return null;
}
function makeEven(n: number) {
  return n % 2 ? n - 1 : n;
} // codecs kiffent les nombres pairs

@Component({
  components: {
    TimelapsePreview,
    TimelapseFilter,
    TimelapseExclusion,
    TimelapsePhoto,
    TimelapseSettings,
    ScenarioModal,
    MotionAnalyze,
    TimelapseIntroOutro,
    RaindropAnalyze,
    vTeleport,
  },
})
export default class Timelapse extends Vue {
  @Prop(String) private id!: string;

  @State('projects', { namespace: 'projects' }) private projects!: Project[];
  @Getter('projectWithId', { namespace: 'projects' }) private projectWithId!: (id: string) => Project;

  @State('premium', { namespace: 'project' }) readonly ownerPremium!: boolean;
  @State('timelapse', { namespace: 'project' }) private timelapse!: Video[];
  @State('loadingTimelapse', { namespace: 'project' }) private loadingTimelapse!: boolean;
  @Getter('timelapseForCamera', { namespace: 'project' }) private timelapseForCamera!: (cameraId: string) => Video;
  @Action('setProject', { namespace: 'project' }) private setProject!: (project: Project) => Promise<void>;
  @Action('loadTimelapse', { namespace: 'project' }) private loadTimelapse!: () => Promise<void>;
  @Action('saveTimelapseConfiguration', { namespace: 'project' }) private saveTimelapseConfiguration!: (
    config: TimelapseConfigurationUpdateRequest
  ) => Promise<void>;

  @Action('findAllFor', { namespace: 'photo' }) private findPhotoAllForProject!: (payload: {
    projectId: string;
    cameraId: string;
  }) => Promise<Photo[]>;

  @State('socket') private socketMessage!: Socket;
  @State('photosByCameraId', { namespace: 'photo' }) private readonly photosByCameraId!: {
    [cameraId: string]: Photo[];
  };
  @Action('connectSocket') private connectSocket!: () => Promise<void>;
  @Action('disconnectSocket') private disconnectSocket!: () => Promise<void>;
  @Mutation('SOCKET_ONMESSAGE') private resetSocketMessage!: (object: string) => void;

  private motionJwt: string | null = null;

  private project: Project | null = null;
  private isStabilizing = false;
  private lastVideo: Video | null = null;
  private timelapseRending = false;
  private leftPanelVisible = true;
  private logoUrl: string | null = null;
  private showLogoActions = false;

  private imagesKey: Photo[] = [];

  private imageWrapCache = new Map<string, { data: Photo }>();

  private stabilizationEnabled = false;

  private previewOpen = true;

  private photoCount = 0;

  private introOutro: any = {
    intro: { bgMode: 'image', bgColor: '#0b1a2a', blur: 18, texts: [], logos: [] },
    outro: { bgMode: 'image', bgColor: '#0b1a2a', blur: 18, texts: [], logos: [] },
    INTRO_DURATION_MS: 2000,
    OUTRO_DURATION_MS: 2000,
  };

  firstPhotoUrl: string | null = null;
  lastPhotoUrl: string | null = null;

  private _imageAR: number | null = null;

  private introOutroMeta: {
    introKey?: string;
    outroKey?: string;
    introDurationMs?: number;
    outroDurationMs?: number;
  } = {};

  public togglePreviewOpen() {
    this.previewOpen = !this.previewOpen;
  }

  // Transitions hauteur (slide)
  public expandEnter(el: HTMLElement) {
    el.style.height = '0';
    el.style.overflow = 'hidden';
    // force next frame
    requestAnimationFrame(() => {
      el.style.transition = 'height 280ms ease';
      el.style.height = el.scrollHeight + 'px';
    });
  }
  public expandAfterEnter(el: HTMLElement) {
    el.style.transition = '';
    el.style.height = 'auto';
    el.style.overflow = '';
  }
  public expandLeave(el: HTMLElement) {
    el.style.height = el.scrollHeight + 'px';
    el.style.overflow = 'hidden';
    // next frame
    requestAnimationFrame(() => {
      el.style.transition = 'height 280ms ease';
      el.style.height = '0';
    });
  }

  private defaultImageModifier = {
    iso: 0,
    brightness: 0,
    contrast: 0,
    saturation: 0,
    white: 0,
    crop: Crop.original,
    erigeWatermark: true,
    watermarkPosition: Position.bottomright,
  };

  get exportAR(): number {
    const stab: any = this.config?.stabilization || {};
    const mode = stab.outARMode || 'image';
    if (mode === '16:9') return 16 / 9;
    if (mode === '4:3') return 4 / 3;
    if (mode === '1:1') return 1;
    if (mode === '9:16') return 9 / 16;
    if (mode === '239:100') return 2.39;
    if (mode === 'custom') {
      const r = parseARString(stab.outARCustom || '');
      if (r) return r;
    }
    // mode 'image' (ou fallback)
    return this._imageAR || 4 / 3;
  }

  // 2) Largeur cible (tu as déjà outputWidth dans ta config)
  get exportW(): number {
    return makeEven(this.config?.outputWidth || 1920);
  }

  // 3) Hauteur déduite du ratio (pair aussi)
  get exportH(): number {
    return makeEven(Math.round(this.exportW / Math.max(0.01, this.exportAR)));
  }

  get motionApiUrl(): string {
    // Priorité: .env, puis window.ENV, sinon fallback
    return (
      (process.env.VUE_APP_MOTION_ANALYZE_URL as string) ||
      ((window as any).ENV?.MOTION_ANALYZE_URL as string) ||
      'https://<ton-api-id>.execute-api.eu-west-3.amazonaws.com/prod/motion/analyze'
    );
  }

  get raindropApiUrl(): string {
    return (
      (process.env.VUE_APP_RAINDROP_URL as string) ||
      ((window as any).ENV?.RAINDROP_ANALYZE_URL as string) ||
      'https://ayg1dycdug.execute-api.eu-west-3.amazonaws.com/prod/raindrop'
    );
  }

  // Bucket S3 des photos
  get motionBucket(): string {
    return 'erige-main-store-prod';
  }

  // Liste de photos au format attendu par <MotionAnalyze>
  // ⬇️ Remplace TON mapping .map(p => ({ key, time, id })) par ceci
  get motionPhotos() {
    const wrappers = this.dataImagesKey as Array<{ data: Photo }>;
    const excluded = new Set(this.config?.photoExclusion ?? []);

    return wrappers
      .map((w) => w.data)
      .filter((p) => !excluded.has(p.id))
      .sort((a, b) => {
        const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : (Number((a as any).createdAt) || 0) * 1000;
        const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : (Number((b as any).createdAt) || 0) * 1000;
        return ta - tb;
      })
      .map((p) => {
        const createdAt: Date =
          p.createdAt instanceof Date ? p.createdAt : new Date((Number((p as any).createdAt) || 0) * 1000);

        // Fallback compat : motiondetect (peut être {M:{...}})
        const mdRaw: any = (p as any).motiondetect || (p as any).motionDetect || null;
        const md = normalizeMd(mdRaw);

        // ✅ Priorité aux champs *à plat*
        const movement = fromDdbNum(
          (p as any).movement ??
            (p as any).score ?? // ancien nom éventuel
            md?.score ??
            0,
          0
        );

        const kpi = {
          motion: fromDdbNum((p as any).motion ?? md?.motion ?? (p as any)?.kpi?.motion ?? 0, 0),
          structure: fromDdbNum((p as any).structure ?? md?.structure ?? (p as any)?.kpi?.structure ?? 0, 0),
          keypoints: fromDdbNum((p as any).keypoints ?? md?.keypoints ?? (p as any)?.kpi?.keypoints ?? 0, 0),
          flow: fromDdbNum((p as any).flow ?? md?.flow ?? (p as any)?.kpi?.flow ?? 0, 0),
          orient: fromDdbNum((p as any).orient ?? md?.orient ?? (p as any)?.kpi?.orient ?? 0, 0),
        };

        return {
          key: p.key,
          time: createdAt,
          id: p.id,
          movement,
          kpi,
          // utile si tu veux inspecter : md normalisé
          motiondetect: md,
        };
      });
  }

  // ROI initial persisté (localStorage par caméra) — sinon []
  get motionInitialRoi() {
    const key = `roi:${this.cameraSelected || ''}`;
    try {
      return JSON.parse(localStorage.getItem(key) || '[]');
    } catch {
      return [];
    }
  }

  // Résolveur d'URL si besoin de signed URL au lieu de public S3
  signedUrlFor = async (key: string) => {
    try {
      // S'assure d'avoir des credentials *ici* (l'enfant peut monter avant `mounted()` du parent)
      const creds = (AWS.config.credentials as any) || (await Auth.currentCredentials());

      const s3 = new AWS.S3({
        region: 'eu-west-3',
        credentials: creds,
        signatureVersion: 'v4',
      });

      const cleanKey = String(key || '').replace(/^\/+/, '');
      // On tente la clé telle quelle (avec "public/"), c'est le cas le plus courant chez toi
      return s3.getSignedUrl('getObject', {
        Bucket: this.motionBucket,
        Key: cleanKey,
        Expires: 900,
      });
    } catch (e) {
      console.error('[signedUrlFor] échec de signature:', e);
      // Laisse le composant enfant passer en fallback Amplify.Storage
      return '';
    }
  };

  private async ensureIdToken() {
    try {
      const session = await Auth.currentSession();
      this.motionJwt = session.getIdToken().getJwtToken();
    } catch (e) {
      console.error('❌ Impossible de récupérer le JWT (IdToken):', e);
      this.motionJwt = null;
    }
  }

  private saveRoi(points: Array<{ x: number; y: number }>) {
    const key = `roi:${this.cameraSelected || ''}`;
    localStorage.setItem(key, JSON.stringify(points || []));
  }

  private get previewKey(): string {
    return this.timelapseRending
      ? this.camera?.id || 'rendering'
      : ((this.lastVideo?.key || this.camera?.id || 'preview') as string);
  }

  private get defaultConfig(): TimelapseConfiguration {
    const isTikee = this.camera?.isTikee ?? false; // ✅ Guard pour camera undefined
    return {
      ...this.defaultImageModifier,
      interval: {
        begin: NaN,
        end: NaN,
      },
      lastNDays: false,
      lastNDaysSpan: 30,
      period: {
        startHour: '00:00',
        endHour: '23:59',
      },
      exclude: [],
      days: [0, 1, 2, 3, 4, 5, 6],
      fps: 25,
      hdr: false,
      // ✅ Force number[] avec defaults (évite undefined)
      blue: [0, 6],
      gain: [isTikee ? 40 : 0, isTikee ? 1800 : 24],
      exposure: [0, isTikee ? 250 : 1000],
      clearness: [0, isTikee ? 60 : 40],
      person: [0, isTikee ? 100 : 100], // ← Ici, explicit
      movement: [0, 100],
      goutte: [0, 100],
      photoExclusion: [] as string[],
      slowmotion: true,
      outputWidth: 3000,
      stabilization: undefined, // ← Sera ajouté dynamiquement si besoin
    };
  }

  private config: TimelapseConfiguration | null = null;

  public get breadcrumbs(): unknown[] {
    const items = [
      {
        text: this.$tc('global.project', 2),
        to: '/',
      },
    ];
    if (this.project !== null) {
      items.push({
        text: this.project.name,
        to: '/project/' + this.project.id,
      });
    }
    items.push({
      text: this.$tc('global.timelapse'),
      to: '#',
    });
    return items;
  }

  public get camerasOptions(): unknown[] {
    const project = this.project;
    const data = [
      ...(project?.cameras?.map((camera) => {
        return {
          text: `${camera.name}`,
          value: camera.id,
        };
      }) ?? []),
      ...(project?.previousCameras?.map((camera) => {
        return {
          text: `${camera.name} (deleted)`,
          value: camera.id,
        };
      }) ?? []),
    ];
    if (data.length > 0) {
      return data;
    } else {
      return [{ text: this.$t('plateformcentral.noCamera').toString(), value: null }];
    }
  }

  resizing = false;

  startResize(event: MouseEvent) {
    this.resizing = true;
    window.addEventListener('mousemove', this.performResize);
    window.addEventListener('mouseup', this.stopResize);
  }

  performResize(event: MouseEvent) {
    if (this.resizing) {
      const leftElement = this.$el.querySelector('.left-film') as HTMLElement;
      if (leftElement !== null) {
        const newWidth = event.clientX - leftElement.getBoundingClientRect().left;
        leftElement.style.width = `${newWidth}px`;
      }
    }
  }

  stopResize() {
    if (this.resizing) {
      this.resizing = false;
      window.removeEventListener('mousemove', this.performResize);
      window.removeEventListener('mouseup', this.stopResize);
    }
  }

  public toggleLeftPanel() {
    this.leftPanelVisible = !this.leftPanelVisible;
  }

  public get timelapseGridStyle() {
    if (this.lastVideo) {
      return {
        'grid-template-areas':
          "'timelapse-filter-film timelapse-photos-film timelapse-photos-film' 'timelapse-preview-film timelapse-photos-film timelapse-photos-film'",
      };
    } else {
      return {
        'grid-template-areas':
          "'timelapse-filter-film timelapse-photos-film timelapse-photos-film' 'timelapse-filter-film timelapse-photos-film timelapse-photos-film'",
      };
    }
  }

  public get cameraSelected(): string | null {
    return (this.$route.query.camera as string) || null;
  }
  public set cameraSelected(s: string | null) {
    logger.info('set cameraSelected', s);
    if (s !== null) {
      this.$router.push({ path: this.$route.path, query: { ...this.$route.query, camera: s } });
    }
  }

  public get camera(): Camera | undefined {
    logger.info('get camera ' + this.cameraSelected);
    return (
      this.project?.cameras?.find((camera: Camera) => camera.id === this.cameraSelected) ||
      this.project?.previousCameras?.find((camera: Camera) => camera.id === this.cameraSelected)
    );
  }

  private get dataImagesKey() {
    const cameraId = this.cameraSelected;

    //console.log('photosByCameraId:', this.photosByCameraId);
    //console.log('cameraSelected:', cameraId);

    //logger.info('dataImagesKey - cameraId:', cameraId);
    //logger.info('dataImagesKey - photosByCameraId:', this.photosByCameraId);

    // Vérifiez si une caméra est sélectionnée et si des photos existent pour cette caméra
    if (!cameraId || !this.photosByCameraId[cameraId]) {
      logger.warn('Aucune photo trouvée pour la caméra sélectionnée.');
      return [];
    }

    let photos = this.photosByCameraId[cameraId];
    //logger.info(`Nombre de photos avant filtrage: ${photos.length}`);

    // Appliquez les filtres si la configuration est présente
    if (this.config !== null) {
      const localConfig = { ...this.config };
      photos = photos.filter((item: Photo) => {
        let pass = true;

        // Filtrer par date
        if (!isNaN(localConfig.interval.begin) && !isNaN(localConfig.interval.end)) {
          const start = new Date(localConfig.interval.begin * 1000);
          const end = new Date(localConfig.interval.end * 1000);
          if (localConfig.interval.begin && localConfig.interval.end && isBefore(start, end)) {
            if (!isWithinInterval(new Date(item.createdAt), { start, end })) {
              logger.debug(`Photo ${item.id} filtrée par date.`);
              pass = false;
            }
          }
        }

        // Filtrer par slowmotion
        if (localConfig.slowmotion === false && item.isSlowmotion === true) {
          logger.debug(`Photo ${item.id} filtrée par slowmotion.`);
          pass = false;
        }

        // Filtrer par jours
        const day = new Date(item.createdAt).getDay();
        if (!localConfig.days.includes(day)) {
          logger.debug(`Photo ${item.id} filtrée par jour.`);
          pass = false;
        }

        // Filtrer par heures
        const date = new Date(item.createdAt);
        const mindate = new Date(date);
        const maxdate = new Date(date);
        if (localConfig.period.startHour) {
          mindate.setHours(
            parseInt(localConfig.period.startHour.split(':')[0] || '0', 10),
            parseInt(localConfig.period.startHour.split(':')[1] || '0', 10)
          );
        }
        if (localConfig.period.endHour) {
          maxdate.setHours(
            parseInt(localConfig.period.endHour.split(':')[0] || '0', 10),
            parseInt(localConfig.period.endHour.split(':')[1] || '0', 10)
          );
        }
        if (isValid(date) && isValid(mindate) && isValid(maxdate)) {
          try {
            if (!isWithinInterval(date, { start: mindate, end: maxdate })) {
              logger.debug(`Photo ${item.id} filtrée par intervalle horaire.`);
              pass = false;
            }
          } catch (err) {
            // Begin is after end
            logger.debug(`Photo ${item.id} filtrée par exception d'intervalle horaire.`);
            pass = false;
          }
        }

        // Filtrer par heures exclues
        if (localConfig.exclude && localConfig.exclude.length > 0) {
          const exclusion = localConfig.exclude[0];
          const excludeStart = new Date(date);
          const excludeEnd = new Date(date);
          excludeStart.setHours(
            parseInt(exclusion.startHour.split(':')[0] || '0', 10),
            parseInt(exclusion.startHour.split(':')[1] || '0', 10)
          );
          excludeEnd.setHours(
            parseInt(exclusion.endHour.split(':')[0] || '0', 10),
            parseInt(exclusion.endHour.split(':')[1] || '0', 10)
          );
          if (isValid(date) && isValid(excludeStart) && isValid(excludeEnd)) {
            try {
              if (isWithinInterval(date, { start: excludeStart, end: excludeEnd })) {
                logger.debug(`Photo ${item.id} filtrée par heures exclues.`);
                pass = false;
              }
            } catch (err) {
              // Begin is after end
              // Continue sans exclure
            }
          }
        }

        // Filtrer par exposition, gain, netteté, etc.
        if (localConfig.hdr && item.hdr !== true) {
          logger.debug(`Photo ${item.id} filtrée par HDR.`);
          pass = false;
        }
        if (Array.isArray(localConfig.exposure) && localConfig.exposure.length === 2 && item.exposure !== undefined) {
          if (item.exposure < localConfig.exposure[0] || item.exposure > localConfig.exposure[1]) {
            logger.debug(`Photo ${item.id} filtrée par exposition.`);
            pass = false;
          }
        }
        if (Array.isArray(localConfig.gain) && localConfig.gain.length === 2 && item.gainControl !== undefined) {
          if (item.gainControl < localConfig.gain[0] || item.gainControl > localConfig.gain[1]) {
            logger.debug(`Photo ${item.id} filtrée par gain.`);
            pass = false;
          }
        }
        if (
          Array.isArray(localConfig.clearness) &&
          localConfig.clearness.length === 2 &&
          item.sharpness !== undefined
        ) {
          if (item.sharpness < localConfig.clearness[0] || item.sharpness > localConfig.clearness[1]) {
            logger.debug(`Photo ${item.id} filtrée par netteté.`);
            pass = false;
          }
        }
        if (Array.isArray(localConfig.person) && localConfig.person.length === 2 && item.person !== undefined) {
          if (item.person < localConfig.person[0] || item.person > localConfig.person[1]) {
            logger.debug(`Photo ${item.id} filtrée par personne.`);
            pass = false;
          }
        }
        if (Array.isArray(localConfig.movement) && localConfig.movement.length === 2 && item.movement !== undefined) {
          if (item.movement < localConfig.movement[0] || item.movement > localConfig.movement[1]) {
            logger.debug(`Photo ${item.id} filtrée par mouvement.`);
            pass = false;
          }
        }
        if (Array.isArray(localConfig.goutte) && localConfig.goutte.length === 2 && item.goutte !== undefined) {
          if (item.goutte < localConfig.goutte[0] || item.goutte > localConfig.goutte[1]) {
            logger.debug(`Photo ${item.id} filtrée par goutte.`);
            pass = false;
          }
        }
        if (Array.isArray(localConfig.blue) && localConfig.blue.length === 2 && item.blueLevel !== undefined) {
          if (item.blueLevel < localConfig.blue[0] || item.blueLevel > localConfig.blue[1]) {
            logger.debug(`Photo ${item.id} filtrée par blueLevel.`);
            pass = false;
          }
        }

        return pass;
      });
      logger.info(`Nombre de photos après filtrage: ${photos.length}`);
    }

    // Retourner les photos filtrées sous forme d'objets avec une propriété 'data'
    return photos.map((item) => {
      let wrapped = this.imageWrapCache.get(item.id);
      if (!wrapped) {
        wrapped = { data: item };
        this.imageWrapCache.set(item.id, wrapped);
      } else {
        // garder la même référence et MAJ la data en place
        wrapped.data = item;
      }
      return wrapped;
    });
  }

  @Watch('dataImagesKey', { deep: true })
  async onImagesChanged() {
    await this.computeFirstLastUrls();
  }

  async mounted(): Promise<void> {
    logger.info('mounted');
    // Creds AWS pour S3 signé si besoin
    configureAWSCredentials();
    // JWT pour l'appel API
    this.ensureIdToken();
    if (this.timelapse.length > 0) {
      this.onTimelapseLoaded(this.timelapse, []);
    }
    if (this.projects.length > 0) {
      this.onProjectsLoaded(this.projects, []);
    }

    //console.log('🔍 dataImagesKey (premier wrapper):', this.dataImagesKey[0]);
    if (this.dataImagesKey[0]) {
      const rawPhoto = this.dataImagesKey[0].data;
      //console.log('🔍 Photo brute depuis parent:', rawPhoto);
      //console.log('🔍 Tous les champs:', Object.keys(rawPhoto));
    }

    // Ajoute la classe pour les styles
    document.body.classList.add('crisp-offset--timelapse');

    // --- Décalage du bouton Crisp ---
    const adjustCrispPosition = () => {
      const crispButton = document.querySelector('#crisp-chatbox-button, .cc-18ov6') as HTMLElement;
      if (crispButton) {
        crispButton.style.bottom = '80px';
        crispButton.style.right = '25px';
      }
    };

    // Observer les changements du DOM
    const observer = new MutationObserver(() => {
      adjustCrispPosition();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Petit fallback au cas où Crisp est déjà présent après un délai
    setTimeout(adjustCrispPosition, 2000);

    //console.log('🔍 Parent mounted - initial dataImagesKey.length:', this.dataImagesKey.length);

    await this.computeFirstLastUrls();
  }

  public beforeDestroy(): void {
    logger.info('beforeDestroy');
    // called when the route that renders this component is about to
    // be navigated away from.
    // has access to `this` component instance.
    this.disconnectSocket();
    document.body.classList.remove('crisp-offset--timelapse');
  }

  async computeFirstLastUrls() {
    const list = (this.dataImagesKey as Array<{ data: any }>).map((w) => w.data);
    if (!list.length) {
      this.firstPhotoUrl = this.lastPhotoUrl = null;
      return;
    }

    // triés dans ton getter motionPhotos; ici on assure
    const first = list[0]?.key || list[0]?.s3Key || list[0];
    const last = list[list.length - 1]?.key || list[list.length - 1]?.s3Key || list[list.length - 1];

    this.firstPhotoUrl = first ? await this.signedUrlFor(String(first)) : null;
    this.lastPhotoUrl = last ? await this.signedUrlFor(String(last)) : null;

    if (this.firstPhotoUrl) {
      const img = new Image();
      img.onload = () => {
        this._imageAR = img.naturalWidth / Math.max(1, img.naturalHeight);
      };
      img.src = this.firstPhotoUrl;
    }
  }

  async handleIntroOutroExport(payload: { introKey?: string; outroKey?: string }) {
    if (!this.config) return;

    if (!this.config.stabilization) {
      this.config.stabilization = new StabilizationConfig();
    }

    const stab = this.config.stabilization;

    if (payload.introKey) {
      stab.introKey = payload.introKey;
    }
    if (payload.outroKey) {
      stab.outroKey = payload.outroKey;
    }

    console.log('🎬 Intro/Outro keys:', {
      introKey: stab.introKey,
      outroKey: stab.outroKey,
    });
  }

  private _lastMotionExcl: { previous: string[]; added: string[] } | null = null;

  private onExcludePhotosFromMotion(ids: string[]): void {
    if (!this.config) return;

    const prev = (this.config.photoExclusion || []).slice();
    const set = new Set(prev);
    const added: string[] = [];

    ids.forEach((id) => {
      if (!set.has(id)) {
        set.add(id);
        added.push(id);
      }
    });

    this.config.photoExclusion = Array.from(set);
    this._lastMotionExcl = { previous: prev, added };
  }

  private undoExcludeFromMotion(): void {
    if (!this.config || !this._lastMotionExcl) return;
    // restauration complète de l’état d’avant
    this.config.photoExclusion = this._lastMotionExcl.previous.slice();
    this._lastMotionExcl = null;
  }

  private triggerLogoUpload() {
    (this.$refs.logoInput as HTMLInputElement).click();
  }

  private async handleLogoUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      console.error('❌ Aucun fichier sélectionné.');
      return;
    }

    try {
      const region = 'eu-west-3';
      const credentials = await Auth.currentCredentials();
      const s3 = new AWS.S3({ region, credentials });

      const ownerID = this.project?.owner?.id;
      const projectID = this.project?.id;
      if (!ownerID || !projectID) {
        console.error("❌ Impossible de récupérer l'ID du projet ou du propriétaire.");
        return;
      }

      const fileName = `logo-${Date.now()}.${file.name.split('.').pop()}`;
      const key = `public/${ownerID}/${projectID}/Logo/${fileName}`;

      const fileBuffer = await file.arrayBuffer();

      await s3
        .putObject({
          Bucket: 'erige-main-store-prod',
          Key: key,
          Body: Buffer.from(fileBuffer),
          ContentType: file.type,
          ACL: 'public-read',
        })
        .promise();

      //console.log('✅ Logo uploadé avec succès:', key);
      this.fetchLogoUrl(); // Met à jour l'affichage après upload
    } catch (error) {
      console.error("❌ Erreur lors de l'upload du logo:", error);
    }
  }

  public async fetchLogoUrl() {
    try {
      if (!this.project || !this.project.owner) {
        console.warn('❌ `project` ou `project.owner` est indéfini. Attente...');
        return;
      }

      const region = 'eu-west-3';
      const credentials = await Auth.currentCredentials();
      const s3 = new AWS.S3({ region, credentials });

      const ownerID = this.project.owner.id;
      const projectID = this.project.id;

      //console.log('🔹 Récupération du logo pour ownerID:', ownerID, 'projectID:', projectID);

      const prefix = `public/${ownerID}/${projectID}/Logo/`;

      const response = await s3
        .listObjectsV2({
          Bucket: 'erige-main-store-prod',
          Prefix: prefix,
        })
        .promise();

      if (response.Contents && response.Contents.length > 0) {
        const lastFile = response.Contents.sort(
          (a, b) => (b.LastModified?.getTime() || 0) - (a.LastModified?.getTime() || 0)
        )[0];

        if (lastFile && lastFile.Key) {
          this.logoUrl = s3.getSignedUrl('getObject', {
            Bucket: 'erige-main-store-prod',
            Key: lastFile.Key,
            Expires: 3600,
          });

          //console.log('✅ URL du logo récupérée :', this.logoUrl);
        }
      } else {
        console.warn('❌ Aucun logo trouvé dans le bucket.');
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du logo :', error);
    }
  }

  private async deleteAllLogos() {
    try {
      if (!this.project || !this.project.owner) return;

      const region = 'eu-west-3';
      const credentials = await Auth.currentCredentials();
      const s3 = new AWS.S3({ region, credentials });

      const ownerID = this.project.owner.id;
      const projectID = this.project.id;
      const prefix = `public/${ownerID}/${projectID}/Logo/`;

      // Liste des fichiers à supprimer
      const response = await s3.listObjectsV2({ Bucket: 'erige-main-store-prod', Prefix: prefix }).promise();
      if (!response.Contents || response.Contents.length === 0) {
        console.warn('❌ Aucun logo à supprimer.');
        return;
      }

      const deleteParams = {
        Bucket: 'erige-main-store-prod',
        Delete: { Objects: response.Contents.map((item) => ({ Key: item.Key! })) },
      };

      await s3.deleteObjects(deleteParams).promise();
      this.logoUrl = null; // Supprime l'affichage du logo après suppression
      //console.log('✅ Tous les logos ont été supprimés.');
    } catch (error) {
      console.error('❌ Erreur lors de la suppression des logos:', error);
    }
  }

  public handleOpenScenarioModal(config: TimelapseConfiguration) {
    if (this.$refs.scenarioModal) {
      (this.$refs.scenarioModal as ScenarioModal).show(config);
    }
  }

  private async onStabilizationEnded() {
    console.log('✅ Stabilization ended — refreshing timelapse');
    this.isStabilizing = false;
    // Now set rendering to false
    this.timelapseRending = false;

    // Recharge la liste des timelapses pour cette caméra
    await this.loadTimelapse();

    // Réassigne le dernier timelapse pour forcer la mise à jour du player
    this.lastVideo = this.timelapseForCamera(this.cameraSelected!);
    console.log('🎥 New lastVideo after stabilization', this.lastVideo);

    // Disconnect socket
    this.disconnectSocket();

    localStorage.removeItem('erige-stabilization');
    window.location.reload();
  }

  @Watch('projects')
  public onProjectsLoaded(val: Project[], oldVal: Project[]): void {
    logger.info('onProjectsLoaded', { val, oldVal });
    this.project = this.projectWithId(this.id);
    this.setProject(this.project);
  }

  @Watch('project')
  public async onProjectLoaded(val: Project | null, oldVal: Project | null): Promise<void> {
    logger.info('onProjectLoaded', { val, oldVal });

    // 🛑 Pendant le rendu, on laisse la config côté UI tranquille
    if (val !== null && !this.timelapseRending) {
      this.onCameraSelectedChange();
    }

    await this.fetchLogoUrl();
  }

  @Watch('timelapse')
  public onTimelapseLoaded(val: Video[], oldVal: Video[]): void {
    logger.info('onTimelapseLoaded', { val, oldVal });
    if (val.length > 0 && this.cameraSelected !== null) {
      this.lastVideo = this.timelapseForCamera(this.cameraSelected);

      // 🛑 NE PAS toucher à this.config pendant le rendu
      if (this.timelapseRending) {
        return;
      }

      if (this.lastVideo?.timelapseConfiguration !== undefined) {
        let timelapseConfiguration: TimelapseConfiguration;
        const rawConfig = this.lastVideo.timelapseConfiguration;
        if (rawConfig && typeof rawConfig === 'object') {
          const jsonStr = JSON.stringify(rawConfig);
          if (jsonStr !== undefined && jsonStr !== 'null' && jsonStr !== 'undefined') {
            timelapseConfiguration = JSON.parse(jsonStr) as TimelapseConfiguration;
          } else {
            timelapseConfiguration = { ...this.defaultConfig };
          }
        } else {
          timelapseConfiguration = { ...this.defaultConfig };
        }

        console.log('🟢 timelapseConfiguration chargée depuis le backend :', timelapseConfiguration);

        timelapseConfiguration.interval.end = endOfDay(timelapseConfiguration.interval.end * 1000).getTime() / 1000;
        if ((timelapseConfiguration as any).stabilization) {
          inflateFlatFromNested((timelapseConfiguration as any).stabilization);
        }
        this.config = Object.assign({ ...this.defaultConfig }, timelapseConfiguration);
      } else {
        this.config = { ...this.defaultConfig };
      }
    }
  }

  @Watch('socketMessage.message')
  public onSocketMessageChange(val: NotificationMessage): void {
    if (val.timelapse !== undefined && val.projectId === this.id && val.cameraId === this.cameraSelected) {
      switch (val.timelapse) {
        case 'stabilizationStarted':
          this.timelapseRending = false;
          logger.info('🎬 Stabilization started in parent (jobId: ' + (val as any).jobId + ')');
          localStorage.setItem(
            'erige-stabilization',
            JSON.stringify({
              cameraId: this.camera?.id,
              projectId: this.project?.id,
              jobId: (val as any).jobId,
              startedAt: Date.now(),
            })
          );
          break;
        case 'timelapseFinalize':
          this.timelapseRending = false;
          break;
        case 'ready':
          this.timelapseRending = false;
          this.previewOpen = true;
          setTimeout(async () => {
            await this.loadTimelapse();
            if (!this.stabilizationEnabled) this.isStabilizing = false;
          }, 200);
          break;
      }
    }
  }

  @Watch('cameraSelected')
  public onCameraSelectedChange(): void {
    if (this.timelapseRending) {
      return;
    }
    logger.info('onCameraSelectedChange', { query: this.cameraSelected });
    if (this.cameraSelected !== null) {
      const indexCamera: number | undefined = this.project?.cameras
        ?.map((c: Camera) => c.id)
        .indexOf(this.cameraSelected);
      const indexPrevious: number | undefined = this.project?.previousCameras
        ?.map((c: Camera) => c.id)
        .indexOf(this.cameraSelected);
      if ((indexCamera !== undefined && indexCamera !== -1) || (indexPrevious !== undefined && indexPrevious !== -1)) {
        this.lastVideo = this.timelapseForCamera(this.cameraSelected) || null;
        this.findPhotoAllForProject({ projectId: this.id, cameraId: this.cameraSelected });
        this.lastVideo = this.timelapseForCamera(this.cameraSelected);
        if (this.lastVideo?.timelapseConfiguration !== undefined) {
          const timelapseConfiguration = JSON.parse(
            JSON.stringify(this.lastVideo.timelapseConfiguration)
          ) as TimelapseConfiguration;
          timelapseConfiguration.interval.end = endOfDay(timelapseConfiguration.interval.end * 1000).getTime() / 1000;
          if ((timelapseConfiguration as any).stabilization) {
            inflateFlatFromNested((timelapseConfiguration as any).stabilization);
          }
          this.config = Object.assign({ ...this.defaultConfig }, timelapseConfiguration);
        } else {
          this.config = { ...this.defaultConfig };
        }
      } else if (this.project?.cameras?.length ?? 0 > 0) {
        this.cameraSelected = this.project?.cameras?.[0].id ?? null;
      } else if (this.project?.previousCameras?.length ?? 0 > 0) {
        this.cameraSelected = this.project?.previousCameras?.[0].id ?? null;
      } else {
        this.cameraSelected = null;
      }
    }
  }

  @Watch('photosByCameraId')
  public onPhotoStoreUpdated(newVal: { [cameraId: string]: Photo[] }, oldVal: { [cameraId: string]: Photo[] }): void {
    logger.info('onPhotoStoreUpdated', { newVal, oldVal });
    if (this.cameraSelected && newVal[this.cameraSelected]) {
      this.imagesKey = newVal[this.cameraSelected].slice(0).sort((photo1: Photo, photo2: Photo) => {
        return photo1.createdAt.getTime() - photo2.createdAt.getTime();
      });
    } else {
      this.imagesKey = [];
    }
    //console.log('🔍 Parent - dataImagesKey.length after filter:', this.dataImagesKey.length);
  }

  private normalizeStabForTypedJSON(cfg: TimelapseConfiguration) {
    const s: any = cfg.stabilization;
    if (!s) return;

    // Normalisation StabilizationConfig
    if (!(s instanceof StabilizationConfig)) {
      const st = Object.assign(new StabilizationConfig(), s || {});
      cfg.stabilization = st as any;
    }

    const stab: any = cfg.stabilization;

    // ✅ Normalisation aussi de temporal sans perdre son contenu
    if (stab.temporal && !(stab.temporal instanceof TemporalFilterConfig)) {
      stab.temporal = Object.assign(new TemporalFilterConfig(), stab.temporal);
    }

    // Ken Burns nested
    buildNestedKenBurnsFromFlat(cfg.stabilization);
  }

  public async save(): Promise<void> {
    this.previewOpen = true;

    this.$nextTick(() => {
      const el = this.$el.querySelector('.timelapsepreview') as HTMLElement | null;
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    if (this.camera !== undefined && this.project !== null && this.config !== null) {
      const timelapseConfiguration = Object.assign({}, this.config);

      if (!timelapseConfiguration.stabilization) {
        timelapseConfiguration.stabilization = new StabilizationConfig();
      }
      const stab = timelapseConfiguration.stabilization as StabilizationConfig;

      const introMs = (this.introOutro && this.introOutro.INTRO_DURATION_MS) ?? 2000;
      const outroMs = (this.introOutro && this.introOutro.OUTRO_DURATION_MS) ?? 2000;

      stab.introDurationMs = introMs;
      stab.outroDurationMs = outroMs;

      const enabled =
        this.introOutro && typeof this.introOutro.INTRO_OUTRO_ENABLED === 'boolean'
          ? this.introOutro.INTRO_OUTRO_ENABLED
          : !!(this.introOutro.INTRO_KEY || this.introOutro.OUTRO_KEY);
      stab.introOutroEnabled = enabled;

      if (!timelapseConfiguration.stabilization) {
        timelapseConfiguration.stabilization = new StabilizationConfig();
      }

      ['person', 'movement', 'goutte', 'blue', 'gain', 'exposure', 'clearness'].forEach((key) => {
        const arr = (timelapseConfiguration as any)[key];
        if (Array.isArray(arr) && (arr[0] === undefined || arr[1] === undefined)) {
          (timelapseConfiguration as any)[key] = [0, 100];
        }
      });

      this.normalizeStabForTypedJSON(timelapseConfiguration);

      timelapseConfiguration.interval.end = endOfDay(timelapseConfiguration.interval.end * 1000).getTime() / 1000;

      this.stabilizationEnabled = !!timelapseConfiguration.stabilization;

      console.log('🧩 Configuration complète envoyée :', JSON.parse(JSON.stringify(timelapseConfiguration)));

      // 🔴 On marque le rendu AVANT de toucher au store
      this.timelapseRending = true;

      // Optionnel mais plus propre : attendre la fin de l’action
      await this.saveTimelapseConfiguration({
        cameraId: this.camera.id,
        projectId: this.project.id,
        timelapseConfiguration,
      });

      this.resetSocketMessage('');
      this.connectSocket();
    } else {
      console.warn('❌ Impossible de créer le timelapse — config, caméra ou projet manquant :', {
        project: this.project,
        camera: this.camera,
        config: this.config,
      });
    }
  }
}
